import { NgOptimizedImage } from "@angular/common";
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, model, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslocoPipe } from "@jsverse/transloco";
import { SvgIconComponent } from "angular-svg-icon";
import { NgxMaskDirective } from "ngx-mask";
import { DirectusTranslationPipe } from "../../../../../../common/pipes/directus-translation.pipe";
import { GetDirectusImagePipe } from "../../../../../../common/pipes/get-directus-image.pipe";
import { IncludesPipe } from "../../../../../../common/pipes/includes.pipe";
import { ItemsCertifications } from "../../../../../api/packbo/models/items-certifications";
import { ItemsProductCategories } from "../../../../../api/packbo/models/items-product-categories";
import { DisplayResultsPipe } from "./pipes/display-results.pipe";

export type SearchType = "subcategory" | "supplier";

export type SearchOutput = {
    search: string | null;
    postalCode: string | null;
    distance: string | null;
    certifications: number[] | null;
};

@Component({
    selector: "packbo-search",
    templateUrl: "./search.template.html",
    imports: [
        SvgIconComponent,
        TranslocoPipe,
        NgxMaskDirective,
        DisplayResultsPipe,
        FormsModule,
        NgOptimizedImage,
        IncludesPipe,
        DirectusTranslationPipe,
        GetDirectusImagePipe,
        IncludesPipe
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SearchComponent {
    public readonly inputSearchType = input.required<SearchType>();
    public readonly subcategories = input<ItemsProductCategories[]>([]);
    public readonly inputResultsLength = input<number>(0);
    public readonly inputItemsCertifications = input<ItemsCertifications[]>([]);
    public readonly hideSubcategory = input<boolean>(false);

    public readonly outputSearch = output<SearchOutput>();

    public readonly searchValue = model<string | null>(null);
    public readonly postalCode = model<string | null>(null);
    public readonly distance = model<{ value: number; label: string } | null>(null);
    public readonly isEcoFriendly = model<boolean>(false);
    public readonly selectedCertificationsIds = model<number[]>([]);
    public readonly selectedSubcategoryId = model<number | null>(null);

    public readonly isDistanceOpen = signal<boolean>(false);
    public readonly isCertificationsOpen = signal<boolean>(false);

    protected readonly selectedCertificationName = computed(() => {
        const selectedIds = this.selectedCertificationsIds();
        const certifications = this.inputItemsCertifications();

        if (selectedIds.length !== 1) {
            return "";
        }

        const certification = certifications.find((cert) => cert.id === selectedIds[0]);
        return certification?.name || "";
    });

    protected readonly distances = (() => {
        const distances = [];
        for (let i = 5; i <= 200; i += 5) {
            distances.push({ value: i, label: `${i}km` });
        }
        return distances;
    })();

    protected selectDistance(distance: { value: number; label: string }): void {
        this.distance.set(distance);
    }

    protected toggleCertification(certificationId: number): void {
        const currentSelections = this.selectedCertificationsIds();
        const isSelected = currentSelections.includes(certificationId);

        if (isSelected) {
            this.selectedCertificationsIds.set(currentSelections.filter((id) => id !== certificationId));
        } else {
            this.selectedCertificationsIds.set([...currentSelections, certificationId]);
        }
    }

    protected setSuppliersBySubcategory(subcategoryId: number | undefined): void {
        if (!subcategoryId) {
            return;
        }

        this.selectedSubcategoryId.set(subcategoryId);
    }

    protected resetSelectedSubcategoryId(): void {
        this.selectedSubcategoryId.set(null);
    }

    protected search(): void {
        const hasSearchTerm = !!this.searchValue();
        const hasPostalCode = !!this.postalCode();
        const hasDistance = !!this.distance();
        const hasCertifications = this.selectedCertificationsIds().length > 0;
        const hasAnyFilters = hasPostalCode || hasDistance || hasCertifications;

        if (!hasSearchTerm && !hasAnyFilters) {
            this.reset();
            this.outputSearch.emit({
                search: null,
                postalCode: null,
                distance: null,
                certifications: null
            });
            return;
        }

        this.outputSearch.emit({
            search: this.searchValue(),
            postalCode: this.postalCode(),
            distance: this.distance()?.value?.toString() ?? null,
            certifications: this.selectedCertificationsIds()
        });
    }

    private reset(): void {
        this.postalCode.set(null);
        this.distance.set(null);
        this.isDistanceOpen.set(false);
        this.isCertificationsOpen.set(false);
        this.selectedCertificationsIds.set([]);
    }
}
